import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { z } from 'zod';
import type { AssistantConversation } from '@aegislab/shared';
import { safeParseStored, safeSet, safeRemove } from '@/shared/lib/storage';

const PersistedAssistantSchema = z.object({
  state: z
    .object({
      unreadConversations: z.array(z.string()).optional(),
      seenAt: z.record(z.string(), z.string()).optional(),
    })
    .optional(),
  version: z.number().optional(),
});

interface AssistantState {
  isOpen: boolean;
  /**
   * narrow 전용 목록 오버레이 플래그 (outside-voice D8 — view:'list'|'chat' 대체).
   * true면 좁은 패널에서 목록을 "잠깐 보기"로 표시 — activeConversationId·isDraft 비파괴.
   * active·draft가 없으면 false여도 목록이 자연 폴백으로 표시된다.
   * persist 제외(partialize 화이트리스트 미포함 = 자동 제외) — 세션 UI 상태.
   */
  listOpen: boolean;
  activeConversationId: string | null;
  isDraft: boolean;
  /** 안 읽은 어시스턴트 메시지가 있는 conversationId 집합 — bubble unread badge 데이터. */
  unreadConversations: Set<string>;
  /**
   * 사용자가 각 conversation을 마지막으로 본 시각(ISO).
   * Why: 소켓 끊긴 동안 도착한 메시지를 재연결/재시작 시 catch-up 하려면 "어디까지 봤는지"
   * 기준이 필요. 서버 `conversation.updatedAt`과 비교해 unread 재구성. (Issue: 오프라인 갭 + 새로고침 손실)
   */
  seenAt: Record<string, string>;
  /**
   * transient — persist 금지(partialize 화이트리스트라 미포함 = 자동 제외).
   * 도깨비 능동 브리핑 5초 말풍선용 표시 상태. 타이머는 AssistantWidget 소유.
   * 새로고침/cross-tab 시 stale 말풍선 부활 0 보장(영속 대상 아님).
   */
  proactiveSpeech: { conversationId: string; title: string; body: string } | null;
  toggle: () => void;
  open: () => void;
  close: () => void;
  openConversation: (id: string) => void;
  openDraft: () => void;
  backToList: () => void;
  /**
   * 목록 "잠깐 보기" — listOpen만 켜고 activeConversationId·isDraft는 보존.
   * Why: 헤더 "이력" 버튼용. active까지 비우는 backToList(삭제·crossTabSync 경로 전용)와
   * 달리 목록 열람이 현재 대화를 파괴하지 않아야 복귀가 가능하다. (Slice 1 — 발견성)
   */
  openList: () => void;
  /** user-level realtime broadcast 도착 시 호출. 패널 닫혀있거나 활성 대화와 다른 경우만. */
  markUnread: (conversationId: string) => void;
  /** 사용자가 conversation을 열었을 때 호출 — 해당 ID를 unread에서 제거. */
  markRead: (conversationId: string) => void;
  clearAllUnread: () => void;
  /**
   * 계정 전환/로그아웃 시 전체 초기화 — persist 대상(unreadConversations·seenAt)까지 비운다.
   * clearAllUnread()는 seenAt을 "여기까지 봤음"으로 남기므로 계정 전환에는 부적합.
   */
  resetForAccountSwitch: () => void;
  /**
   * 서버 conversations 목록과 로컬 seenAt을 비교해 unread 상태 재구성.
   * Why: 소켓이 끊긴 동안 도착한 메시지(broadcast 누락)를 재연결/앱 시작 시 복구.
   */
  reconcileUnread: (conversations: Pick<AssistantConversation, 'id' | 'updatedAt'>[]) => void;
  /** 도깨비 능동 브리핑 도착 시 말풍선 발화 — 연속 수신은 새 객체로 교체(타이머 자동 리셋). */
  speak: (payload: { conversationId: string; title: string; body: string }) => void;
  /** 말풍선 dismiss. 무인자=무조건 clear, id 지정=현재 발화 대화와 일치할 때만(stale-close 방어). */
  dismissSpeech: (conversationId?: string) => void;
}

const STORAGE_KEY = 'dokkaebi-unread-state';

// Why: broadcast로 들어온 unread는 seenAt이 없을 수 있는데, reconcile이 !lastSeen을
// baseline으로 처리(false positive 차단)하는 룰과 충돌해 unread가 사라지는 회귀가 있음.
// markUnread 시 sentinel을 박아두면 reconcile에서 updatedAt > sentinel(epoch) 이 항상
// 성립해 broadcast unread가 보존됨. 사용자가 markRead로 열어보면 실제 시각으로 덮임.
const UNREAD_SENTINEL = '1970-01-01T00:00:00Z';

export const useAssistantStore = create<AssistantState>()(
  persist(
    (set) => ({
      isOpen: false,
      listOpen: false,
      activeConversationId: null,
      isDraft: true,
      unreadConversations: new Set(),
      seenAt: {},
      proactiveSpeech: null,
      toggle: () => set((s) => ({ isOpen: !s.isOpen })),
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      openConversation: (id) =>
        set((s) => {
          const next = new Set(s.unreadConversations);
          next.delete(id);
          return {
            activeConversationId: id,
            listOpen: false,
            isDraft: false,
            isOpen: true,
            unreadConversations: next,
            seenAt: { ...s.seenAt, [id]: new Date().toISOString() },
            // 그 대화로 진입하면 말풍선도 정리(배지·말풍선 동시 해제 단일화).
            proactiveSpeech: s.proactiveSpeech?.conversationId === id ? null : s.proactiveSpeech,
          };
        }),
      openDraft: () => set({ activeConversationId: null, listOpen: false, isDraft: true }),
      // active 클리어 + listOpen=false — 목록은 "active·draft 없음" 자연 폴백으로 표시.
      backToList: () => set({ activeConversationId: null, listOpen: false, isDraft: false }),
      openList: () => set({ listOpen: true }),
      markUnread: (id) =>
        set((s) => {
          if (s.unreadConversations.has(id)) return s;
          const next = new Set(s.unreadConversations);
          next.add(id);
          // 처음 보는 대화에 대한 broadcast인 경우 reconcile이 baseline으로 정리하지 않도록
          // sentinel 기록. 이미 seenAt이 있으면(사용자가 한 번이라도 열어봤음) 보존.
          return {
            unreadConversations: next,
            seenAt: s.seenAt[id] ? s.seenAt : { ...s.seenAt, [id]: UNREAD_SENTINEL },
          };
        }),
      markRead: (id) =>
        set((s) => {
          const inUnread = s.unreadConversations.has(id);
          const next = inUnread ? new Set(s.unreadConversations) : s.unreadConversations;
          if (inUnread) next.delete(id);
          return {
            unreadConversations: next,
            seenAt: { ...s.seenAt, [id]: new Date().toISOString() },
            // 활성 대화 시청 중이면 말풍선도 정리(소켓 else 분기가 markRead만 호출하면 됨).
            proactiveSpeech: s.proactiveSpeech?.conversationId === id ? null : s.proactiveSpeech,
          };
        }),
      clearAllUnread: () =>
        set((s) => {
          if (s.unreadConversations.size === 0) return s;
          // Why: Set만 비우면 소켓 재연결마다 도는 reconcileUnread(updatedAt > seenAt)가
          // 방금 비운 대화를 부활시킴 — 비운 대화들의 seenAt을 now로 올려 "여기까지 봤음" 박제.
          const now = new Date().toISOString();
          const seenAtPatch: Record<string, string> = {};
          for (const id of s.unreadConversations) seenAtPatch[id] = now;
          return {
            unreadConversations: new Set<string>(),
            seenAt: { ...s.seenAt, ...seenAtPatch },
          };
        }),
      resetForAccountSwitch: () =>
        set({
          isOpen: false,
          listOpen: false,
          activeConversationId: null,
          isDraft: true,
          unreadConversations: new Set<string>(),
          seenAt: {},
          proactiveSpeech: null,
        }),
      reconcileUnread: (conversations) =>
        set((s) => {
          // Why: 서버 conversations 목록을 ground-truth로 unread Set 재구성.
          //  - !lastSeen 케이스를 unread로 간주하던 기존 룰은 localStorage 클리어/새 디바이스/
          //    시크릿 모드에서 가시 대화 전체가 false positive로 폭증하는 폭탄이었음. 처음 보는
          //    대화는 baseline(seenAt = updatedAt)만 박고 unread 등록은 broadcast의 markUnread에
          //    위임한다.
          //  - 서버 목록에 없는 ID는 next에 안 담겨 자동 prune (삭제됐거나 다른 agent로 이관된
          //    orphan ID 정리).
          const next = new Set<string>();
          const seenAtPatch: Record<string, string> = {};

          for (const c of conversations) {
            if (c.id === s.activeConversationId) continue;
            const lastSeen = s.seenAt[c.id];
            if (!lastSeen) {
              seenAtPatch[c.id] = c.updatedAt;
            } else if (new Date(c.updatedAt).getTime() > new Date(lastSeen).getTime()) {
              next.add(c.id);
            }
          }

          // 변화 없으면 동일 참조 유지 → selector 불필요 리렌더 차단.
          const sameSet =
            next.size === s.unreadConversations.size &&
            [...next].every((id) => s.unreadConversations.has(id));
          const samePatch = Object.keys(seenAtPatch).length === 0;
          if (sameSet && samePatch) return s;

          return {
            unreadConversations: sameSet ? s.unreadConversations : next,
            seenAt: samePatch ? s.seenAt : { ...s.seenAt, ...seenAtPatch },
          };
        }),
      speak: (payload) => set({ proactiveSpeech: payload }),
      dismissSpeech: (id) =>
        set((s) =>
          // 무인자 → 무조건 clear. id 지정 → 현재 발화 중인 대화와 일치할 때만 clear.
          // (A 타이머 만료 시 이미 B 말풍선으로 교체됐으면 no-op — stale-close 방어.)
          !id
            ? { proactiveSpeech: null }
            : s.proactiveSpeech?.conversationId === id
              ? { proactiveSpeech: null }
              : s
        ),
    }),
    {
      name: STORAGE_KEY,
      // Why: Set은 JSON 직렬화 불가 → Array로 왕복. partialize로 영속 대상 한정 (UI state 제외).
      partialize: (state) => ({
        unreadConversations: state.unreadConversations,
        seenAt: state.seenAt,
      }),
      storage: {
        getItem: (name) => {
          const raw = localStorage.getItem(name);
          if (!raw) return null;
          const parsed = safeParseStored(raw, PersistedAssistantSchema);
          if (!parsed) return null;
          return {
            state: {
              unreadConversations: new Set(parsed.state?.unreadConversations ?? []),
              seenAt: parsed.state?.seenAt ?? {},
            },
            version: parsed.version,
          };
        },
        setItem: (name, value) => {
          const state = value.state;
          safeSet(name, {
            state: {
              unreadConversations: Array.from(state.unreadConversations),
              seenAt: state.seenAt,
            },
            version: value.version,
          });
        },
        removeItem: (name) => safeRemove(name),
      },
    }
  )
);
